import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext } from 'react';
import { Popper, Grow, ClickAwayListener, makeStyles, } from '@material-ui/core';
import { useId } from '@reach/auto-id';
import { PrimaryButton } from '../Button/Button';
import { Paper } from '../Paper/Paper';
import { MenuList, MenuItem } from '../Menu/Menu';
// Map of valid transition types to transition origins.
// keys expected to be a substring of one of the values of
// PopperPlacementType
const transitionMap = new Map([
    ['bottom', 'center top'],
    ['left', 'right center'],
    ['right', 'left center'],
    ['top', 'center bottom'],
]);
const DropdownContext = React.createContext(null);
/**
 * Wrapper component around MUI MenuItem.
 * Provides default behavior of MenuItem, but ensures it can close the wrapping menu if desired.
 */
export const ButtonDropdownItem = React.forwardRef((props, ref) => {
    const dropdownContext = useContext(DropdownContext);
    const wrappedOnClick = (event) => {
        if (props.onClick) {
            props.onClick(event);
        }
        if (dropdownContext && dropdownContext.closeOnItemClick) {
            dropdownContext.close();
        }
    };
    return (_jsx(MenuItem, Object.assign({}, props, { ref: ref, button: true, onClick: wrappedOnClick }, { children: props.children }), void 0));
});
const useStyles = makeStyles(() => ({
    dropdownRoot: {
        zIndex: 1300, // https://material-ui.com/customization/z-index/#z-index aligning with zIndex suggestions of MUI
    },
}));
/**
 * ButtonDropdown
 * Largely a parameterized wrapper of the snippet shown here by MUI: https://material-ui.com/components/menus/#menulist-composition
 * It differs from the core Menu component in that it uses <Popper> instead of <Popover> which allows for:
 * 1. Easier relative positioning
 * 2. No blocking of scroll.
 */
export function ButtonDropdown(_a) {
    var { placement: menuPlacement = 'bottom', label, closeOnItemClick = true, children } = _a, btnProps = __rest(_a, ["placement", "label", "closeOnItemClick", "children"]);
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const btnRef = React.useRef(null);
    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = () => {
        setOpen(false);
    };
    function handleListKeyDown(event) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        }
    }
    // return focus to the button when we transitioned from open -> !open
    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        // if the dropdown was open, and now it's not, focus the button.
        if (prevOpen.current === true && open === false) {
            // eslint-disable-next-line
            btnRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);
    // ensure grow transition origin is appropriate for direction
    const mapTransitionToPlacement = (placement) => {
        return transitionMap.get(placement.split('-')[0]);
    };
    const idLabel = useId();
    return (_jsxs(_Fragment, { children: [_jsx(PrimaryButton, Object.assign({ ref: btnRef, "aria-haspopup": "true", "aria-controls": open ? `button-dropdown-${idLabel}` : undefined, onClick: handleToggle }, btnProps, { children: label }), void 0),
            _jsx(Popper, Object.assign({ open: open, anchorEl: btnRef.current, role: undefined, transition: true, disablePortal: true, placement: menuPlacement, className: classes.dropdownRoot }, { children: ({ TransitionProps, placement }) => (_jsx(Grow, Object.assign({}, TransitionProps, { style: {
                        transformOrigin: mapTransitionToPlacement(placement),
                    } }, { children: _jsx(Paper, { children: _jsx(DropdownContext.Provider, Object.assign({ value: { close: handleClose, closeOnItemClick } }, { children: _jsx(ClickAwayListener, Object.assign({ onClickAway: handleClose }, { children: _jsx(MenuList, Object.assign({ autoFocusItem: open, id: open ? `button-dropdown-${idLabel}` : undefined, onKeyDown: handleListKeyDown }, { children: children }), void 0) }), void 0) }), void 0) }, void 0) }), void 0)) }), void 0)] }, void 0));
}
//# sourceMappingURL=ButtonDropdown.js.map